import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
// Imported, not a /public path — see note in the commit: a bad public path
// 404s silently on a case-sensitive filesystem, an import fails the build.
import logo from '../assets/logo.gif';


/*
 * Engine first, then the surfaces that sit on it, then the gallery — which is
 * what any of them produces, so it comes last. Deliberate and not alphabetical.
 *
 * Six entries fill the inline row at 768px without overflowing. A seventh would
 * not: it needs the inline breakpoint moved to lg, which hides the nav behind
 * the menu on tablets. Worth knowing before anything else is added here.
 */
const NAV = [
  { to: '/technology',     label: 'Technology' },
  { to: '/desktop',        label: 'Desktop App' },
  { to: '/reco',           label: 'Reco' },
  { to: '/sdk',            label: 'SDK' },
  { to: '/spatial-models', label: 'Spatial Models' },
  { to: '/marketing-gallery', label: 'Gallery' },
];

const linkClasses = (active: boolean) =>
  `rounded py-2 font-brand font-light tracking-wide transition-colors focus:outline-none `
  + `focus-visible:ring-2 focus-visible:ring-crane-500 `
  + (active ? 'text-crane-500' : 'text-ink-100 hover:text-crane-500');

/** Shared header for the public pages: landing, the product pages, and the gallery. */
const SiteHeader: React.FC = () => {
  const { pathname } = useLocation();
  /*
   * Four links plus the call to action do not fit a phone. Inline they overflowed
   * the nav below 640px and the whole document below 480px — which is the width
   * that matters most here, since the product sheets carry QR codes and most of
   * that traffic arrives on a handset.
   *
   * So the links collapse behind a menu on small screens. With the demo button
   * gone the bar is just the mark and the menu, which fits anywhere.
   */
  const [menuOpen, setMenuOpen] = useState(false);

  // A navigation closes it; so does Escape, for anyone who opened it by keyboard.
  useEffect(() => { setMenuOpen(false); }, [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-ink-800 bg-ink-950/90 px-3 py-3 backdrop-blur-lg sm:px-6 sm:py-4">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
        <Link to="/" className="flex flex-shrink-0 items-center gap-1.5 sm:gap-3">
          <img src={logo} alt="CR3ATE.AI" className="h-6 sm:h-8" />
          {/* On `ember`, not the accent: the wordmark is brand and should not
              follow a UI palette change. Swap to text-crane-500 to bring the
              mark in line with the cyan accent. */}
          <h1 className="font-brand text-body font-light tracking-wide text-ember-500 sm:text-title">
            CR3ATE.AI
          </h1>
        </Link>

        <div className="flex items-center gap-3 sm:gap-8">
          {/* Inline from md up, where all four fit without crowding the mark. */}
          <nav className="hidden items-center gap-6 md:flex lg:gap-8">
            {NAV.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                aria-current={pathname === to ? 'page' : undefined}
                className={`${linkClasses(pathname === to)} whitespace-nowrap text-body lg:text-title`}
              >
                {label}
              </Link>
            ))}
          </nav>


          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="site-nav-mobile"
            className="-mr-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-ink-100 transition-colors hover:bg-ink-800 hover:text-crane-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500 md:hidden"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Below the bar rather than over the page: the header is sticky, so an
          overlay would have to manage scroll locking to behave, and this has to
          be right on five pages with very different scroll behaviour. */}
      <nav
        id="site-nav-mobile"
        hidden={!menuOpen}
        className="mx-auto mt-3 max-w-7xl border-t border-ink-800 pt-2 md:hidden"
      >
        <ul className="flex flex-col">
          {NAV.map(({ to, label }) => (
            <li key={to}>
              <Link
                to={to}
                aria-current={pathname === to ? 'page' : undefined}
                className={`${linkClasses(pathname === to)} block px-1 py-3 text-body`}
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </header>
  );
};

export default SiteHeader;
